import { useEffect, useRef, useState } from "react";
import { LOOKUP_SYNC_EVENT } from "../services/offlineLookupSync";

const DEFAULT_AUTO_HIDE_PHASES = ["completed", "skipped"];

const getFailedCount = (snapshot) => {
  if (Array.isArray(snapshot?.failed)) {
    return snapshot.failed.length;
  }

  if (!Array.isArray(snapshot?.statuses)) {
    return 0;
  }

  return snapshot.statuses.filter((status) => status?.status === "failed").length;
};

export const useLookupSyncStatus = ({
  autoHideDelayMs = 4000,
  autoHidePhases = DEFAULT_AUTO_HIDE_PHASES,
} = {}) => {
  const [snapshot, setSnapshot] = useState(null);
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef(null);
  const autoHideDelayMsRef = useRef(autoHideDelayMs);
  const autoHidePhasesRef = useRef(autoHidePhases);

  useEffect(() => {
    autoHideDelayMsRef.current = autoHideDelayMs;
  }, [autoHideDelayMs]);

  useEffect(() => {
    autoHidePhasesRef.current = autoHidePhases;
  }, [autoHidePhases]);

  useEffect(() => {
    const handleStatusUpdate = (event) => {
      const nextSnapshot = event?.detail;
      if (!nextSnapshot) {
        return;
      }

      setSnapshot(nextSnapshot);
      setVisible(true);

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }

      const hasFailures = getFailedCount(nextSnapshot) > 0;
      const shouldAutoHide = autoHidePhasesRef.current.includes(nextSnapshot.phase) && !hasFailures;

      if (shouldAutoHide) {
        hideTimerRef.current = window.setTimeout(() => {
          setVisible(false);
        }, autoHideDelayMsRef.current);
      }
    };

    window.addEventListener(LOOKUP_SYNC_EVENT, handleStatusUpdate);

    return () => {
      window.removeEventListener(LOOKUP_SYNC_EVENT, handleStatusUpdate);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  return {
    snapshot,
    visible,
    setVisible,
    phase: snapshot?.phase || "idle",
    queued: snapshot?.queued || [],
    syncing: snapshot?.syncing || [],
    skipped: snapshot?.skipped || [],
    synced: snapshot?.synced || [],
    failed: snapshot?.failed || [],
  };
};

export default useLookupSyncStatus;
